import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { flatMap, map, some } from "lodash";
import React, { useRef, useEffect, useCallback, useReducer } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
} from "@tanstack/react-table";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import queryString from "query-string";

import NextSection from "~/components/buttons/NextSection";

import ErrorBoundary from "~/components/ErrorBoundary";
import ProductTableFiltersSelection from "~/components/ProductTableFiltersSelection";
import ProductTableMain from "~/components/ProductTableMain";
import TablePagination, {
    PAGINATION_AMOUNTS,
} from "~/components/TablePagination";
import FaIcon from "~/components/FaIcon";

import useOnWindowResize from "~/hooks/useOnWindowResize";
import { useProductImageContext } from "~/context/ProductImageContext";
import { useLocationContext } from "~/context/LocationContext";

import { getFiltersFromQueryString } from "~/utilities/filters";
import { setScrollStates } from "~/utilities/tableHelpers";

import { reducer, initialState, setInitState } from "~/reducers/productTable";

import useStringTranslation from "~/hooks/translations/useStringTranslation";

import { screenReaderText } from "~/styles/screenReaderText";

export const BreakContainer = styled.div`
    margin-left: 0;
    margin-right: 0;

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        margin-left: ${({ filterHeader, theme }) =>
            filterHeader
                ? `calc(((100vw - 100%) / -2) + ${theme.table.breakContainerSpacing})`
                : `0`};
        margin-right: ${({ scrollBarHasWidth, filterHeader, theme }) =>
            filterHeader
                ? `calc(((100vw - 100%) / -2) ${
                      scrollBarHasWidth
                          ? `+ ${theme.table.breakContainerOffset}`
                          : `+ ${theme.table.breakContainerSpacing}`
                  })`
                : "0"};

        @-moz-document url-prefix() {
            margin-right: ${({ filterHeader, theme }) =>
                filterHeader
                    ? `calc(((100vw - 100%) / -2) + ${theme.table.breakContainerSpacing})`
                    : `0`};
        }
    }
`;

export const FiltersWrapper = styled.div`
    background-color: ${({ theme, backgroundColor }) =>
        backgroundColor === "gray"
            ? theme.palette.grayThree
            : theme.palette.white};
    padding-bottom: ${({ hasFilters }) => (hasFilters ? `15px` : 0)};
    display: flex;
    flex-direction: ${({ isRefDesign }) => (isRefDesign ? `row` : `column`)};
    align-items: ${({ isRefDesign }) => (isRefDesign ? `flex-end` : `stretch`)};
    flex-wrap: wrap;
    gap: ${({ hasFilters }) => (hasFilters ? `15px` : 0)};

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        flex-wrap: nowrap;
    }
`;

const FilterWrap = ({
    backgroundColor,
    hasFilters,
    wrapRef,
    children,
    filterHeader,
}) => {
    let filterWrap = null;
    if (filterHeader) {
        filterWrap = (
            <FiltersWrapper {...{ backgroundColor, hasFilters }} ref={wrapRef}>
                {children}
            </FiltersWrapper>
        );
    }
    return filterWrap;
};

export const FilterSelectionWrapper = styled.div`
    flex: 1;
    width: 100%;
`;

export const ArrowIcon = styled(FaIcon)`
    margin-left: 10px;
`;

export const TableContainer = styled.div``;

export const ScrollBarWrapper = styled.div`
    overflow: auto;
    height: ${({ theme }) => theme.table.scrollBarWidth};
    scrollbar-width: ${({ theme }) => theme.table.scrollBarWidth};
    scrollbar-color: ${({ theme }) => theme.palette.graySix};
    position: sticky;
    bottom: 0;
    z-index: 2;
    margin-bottom: ${({ theme }) => theme.layout.paragraphSpacing};

    &::-webkit-scrollbar {
        width: ${({ theme }) => theme.table.scrollBarWidth};
        height: ${({ theme }) => theme.table.scrollBarWidth};
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${({ theme }) => theme.palette.graySix};
        border-radius: ${({ theme }) => theme.table.scrollBarWidth};
    }

    &::-webkit-scrollbar-corner {
        background-color: ${({ theme, backgroundColor }) =>
            theme.sectionBackgroundColor[backgroundColor]};
    }
`;

export const ScrollBar = styled.div`
    font-size: 1px;
    margin: 0;
    padding: 0;
    height: 1px;
    overflow: hidden;
`;

export const ProductFiltersLiveAnnouncement = styled.div`
    ${screenReaderText}
`;

const ScreenReaderText = styled.h2`
    ${screenReaderText}
`;

const ProductTable = ({
    ariaLabel = "",
    backgroundColor,
    columns = [],
    data = [],
    filterOptions = [],
    fixed = false,
    languageCode,
    onPageChange = null,
    productHeaders = [],
    smallTableHeaders = false,
    tableOptions = {},
    withPagination = false,
    filterHeader = true,
    firstColumnStaticOnMobile = false,
    currentTab = "",
    tabChanged = false,
}) => {
    const { location } = useLocationContext();

    const tableRef = useRef();
    const scrollBarRef = useRef();
    const bottomRef = useRef();
    const filterWrapperRef = useRef();
    const paginationWrapperRef = useRef();

    const { endOfSectionText } = useStringTranslation([
        { text: "End of Section", handle: "endOfSectionText" },
    ]);

    const [reducerState, dispatch] = useReducer(reducer, initialState, () =>
        setInitState(),
    );

    const { useStickyScrollbar, verticalScrollExists, isMac, tableWrapperRef } =
        reducerState;

    const { setHideImages } = useProductImageContext();

    const defaultPageSize = data?.length || 0;

    const {
        getHeaderGroups,
        getRowModel,
        getState,
        setColumnFilters,
        getCanPreviousPage,
        getCanNextPage,
        nextPage,
        getPageCount,
        previousPage,
        setPageSize,
        getPageOptions,
        setPageIndex,
    } = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            columnVisibility: tableOptions.initialState.hiddenColumns,
            sorting: tableOptions.initialState.sortBy,
            pagination: {
                pageSize: withPagination
                    ? PAGINATION_AMOUNTS[0]
                    : defaultPageSize,
            },
        },
    });

    const filtersCount = flatMap(
        getState().columnFilters,
        (filter) => filter.value,
    ).length;

    const rows = getRowModel().rows;

    const filterLiveContent = filtersCount
        ? rows.length
            ? `${filtersCount} filters applied. Showing ${rows.length} results.`
            : `${filtersCount} filters applied. No matching results`
        : `No filters selected, showing all ${rows.length} products`;

    useEffect(() => {
        const queryStringFilters = queryString.parse(location.search);

        const filters = getFiltersFromQueryString(
            queryStringFilters,
            filterOptions,
        );

        setColumnFilters(filters);
    }, [location.search, filterOptions, setColumnFilters]);

    useEffect(() => {
        if (currentTab && tabChanged) {
            setColumnFilters([]);
        }
    }, [currentTab, tabChanged, setColumnFilters]);

    useEffect(() => {
        if (tableRef.current?.parentNode) {
            dispatch({
                type: "SET_WRAPPER_REF",
                tableWrapperRef: tableRef.current.parentNode,
            });
        }
    }, [tableRef]);

    const checkScroll = useCallback(() => {
        setScrollStates(
            tableWrapperRef,
            useStickyScrollbar,
            verticalScrollExists,
            dispatch,
            tableRef,
            scrollBarRef,
        );
    }, [tableWrapperRef, useStickyScrollbar, verticalScrollExists]);

    useEffect(() => {
        checkScroll();
    }, [checkScroll]);

    useOnWindowResize(() => setHideImages(true), false);

    useOnWindowResize(() => checkScroll());

    const onPageIndexChange = (index) => {
        if (typeof onPageChange === "function") {
            onPageChange(index);
        }
    };

    return (
        <ErrorBoundary>
            <BreakContainer
                scrollBarHasWidth={verticalScrollExists && isMac}
                filterHeader={filterHeader}
            >
                <ProductFiltersLiveAnnouncement
                    aria-live="assertive"
                    aria-atomic="true"
                >
                    {filterLiveContent}
                </ProductFiltersLiveAnnouncement>
                <FilterWrap
                    {...{ backgroundColor, filterHeader }}
                    hasFilters={filterOptions.length}
                    wrapRef={filterWrapperRef}
                >
                    <FilterSelectionWrapper>
                        <ProductTableFiltersSelection
                            tableState={getState()}
                            tableRows={rows}
                            headers={productHeaders}
                            {...{
                                ariaLabel,
                                filterOptions,
                                languageCode,
                                setAllFilters: (newFilters) => {
                                    setColumnFilters(newFilters);
                                },
                                setFilter: (id, value) => {
                                    setColumnFilters((oldColumns) => {
                                        const hasFilter = some(oldColumns, {
                                            id,
                                        });

                                        if (hasFilter) {
                                            return map(oldColumns, (filter) => {
                                                if (filter.id === id) {
                                                    return {
                                                        ...filter,
                                                        value,
                                                    };
                                                }
                                                return filter;
                                            });
                                        }

                                        return [...oldColumns, { id, value }];
                                    });
                                },
                                currentTab,
                            }}
                        />
                    </FilterSelectionWrapper>
                    <NextSection
                        hasFilters={filterOptions.length}
                        targetRef={bottomRef.current}
                        scrollBarHasWidth={verticalScrollExists}
                    />
                </FilterWrap>

                <TableContainer>
                    <ScrollSync>
                        <ErrorBoundary>
                            <ProductTableMain
                                {...{
                                    ariaLabel,
                                    backgroundColor,
                                    fixed,
                                    headerGroups: getHeaderGroups(),
                                    rows: rows,
                                    smallTableHeaders,
                                    useStickyScrollbar,
                                    withPagination,
                                    firstColumnStaticOnMobile,
                                    filterWrapperRef,
                                    paginationWrapperRef,
                                }}
                                ref={tableRef}
                            />

                            {useStickyScrollbar ? (
                                <ScrollSyncPane>
                                    <ScrollBarWrapper
                                        backgroundColor={backgroundColor}
                                    >
                                        <ScrollBar
                                            ref={scrollBarRef}
                                        ></ScrollBar>
                                    </ScrollBarWrapper>
                                </ScrollSyncPane>
                            ) : null}
                        </ErrorBoundary>
                    </ScrollSync>
                </TableContainer>
            </BreakContainer>

            {withPagination ? (
                <div ref={paginationWrapperRef}>
                    <TablePagination
                        {...{
                            canPreviousPage: getCanPreviousPage(),
                            canNextPage: getCanNextPage(),
                            gotoPage: setPageIndex,
                            pageCount: getPageCount(),
                            pageOptions: getPageOptions(),
                            nextPage: nextPage,
                            previousPage: previousPage,
                            onPageIndexChange,
                            setPageSize,
                            tableOptions,
                            tableWrapperRef,
                        }}
                        pageIndex={getState().pagination.pageIndex}
                        pageSize={getState().pagination.pageSize}
                        scrollToRef={tableWrapperRef}
                    />
                </div>
            ) : null}
            <ScreenReaderText ref={bottomRef}>
                {endOfSectionText}
            </ScreenReaderText>
        </ErrorBoundary>
    );
};

ProductTable.propTypes = {
    ariaLabel: PropTypes.string,
    backgroundColor: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.shape({})),
    data: PropTypes.arrayOf(PropTypes.shape({})),
    filterHeader: PropTypes.bool,
    filterOptions: PropTypes.arrayOf(PropTypes.shape({})),
    fixed: PropTypes.bool,
    languageCode: PropTypes.string,
    onPageChange: PropTypes.func,
    productHeaders: PropTypes.arrayOf(PropTypes.string),
    smallTableHeaders: PropTypes.bool,
    tableOptions: PropTypes.shape({}),
    withPagination: PropTypes.bool,
    firstColumnStaticOnMobile: PropTypes.bool,
    currentTab: PropTypes.string,
    tabChanged: PropTypes.bool,
};

export default ProductTable;
